import { createOrganizationVariable } from "./controllers/organizationVariables";
import { VOICIFY_EMAIL_STORE } from "../constants/Keys";

export const SHARED_SUPPORT_ORGANIZATION_ID = "4143a851-53e8-40a3-8937-1e009b46c640";
export const SUPPORT_LOG_VARIABLE_PREFIX = "voicify_controls_support_log_v1_";
export const SUPPORT_LOG_SCHEMA = "voicify.controls.supportLog";

export interface SparkFormFieldChange {
    fieldName: string;
    oldValue: string;
    newValue: string;
    state: "Added" | "Modified" | "Removed";
}

export interface SparkFormSupportHistoryRequest {
    applicationId: string;
    applicationName?: string;
    organizationId: string;
    organizationName?: string;
    formName?: string;
    changes: SparkFormFieldChange[];
    action?: "saved" | "published";
}

const createId = (): string => `support-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getAuthorEmail = (): string => {
    if (typeof localStorage === "undefined") {
        return "Unknown user";
    }

    return (localStorage.getItem(VOICIFY_EMAIL_STORE) || "").trim().toLowerCase() || "Unknown user";
};

const sanitizeNamePart = (value: string): string => value.replace(/[^A-Za-z0-9]/g, "");

export const getSparkFormSupportLogPrefix = (applicationId: string): string =>
    `${SUPPORT_LOG_VARIABLE_PREFIX}spark_${sanitizeNamePart(applicationId)}_`;

const createVariableName = (prefix: string, createdAt: string, id: string): string => {
    const timestamp = createdAt.replace(/[^0-9]/g, "").slice(0, 17) || String(Date.now());
    const suffix = sanitizeNamePart(id).slice(-16);
    return `${prefix}${timestamp}_${suffix}`;
};

const truncate = (value: string, maxLength: number): string => {
    const normalized = (value || "").trim();
    return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
};

export const formatSparkFormSupportLogDetail = (changes: SparkFormFieldChange[]): string => {
    const visibleChanges = changes.slice(0, 30).map(change => {
        const fieldName = truncate(change.fieldName || "Untitled field", 120);
        const oldValue = truncate(change.oldValue, 240);
        const newValue = truncate(change.newValue, 240);

        if (change.state === "Added") {
            return `${fieldName}: added “${newValue}”`;
        }
        if (change.state === "Removed") {
            return `${fieldName}: removed “${oldValue}”`;
        }
        return `${fieldName}: “${oldValue}” → “${newValue}”`;
    });

    if (changes.length > visibleChanges.length) {
        visibleChanges.push(`+${changes.length - visibleChanges.length} more changes`);
    }

    return visibleChanges.join("\n");
};

const writeSupportHistoryEntry = async (
    targetOrganizationId: string,
    variablePrefix: string,
    request: SparkFormSupportHistoryRequest
) => {
    const createdAt = new Date().toISOString();
    const id = createId();
    const action = request.action || "saved";
    const formName = (request.formName || "Spark form").trim();
    const entry = {
        schema: SUPPORT_LOG_SCHEMA,
        version: 1,
        id,
        createdAt,
        authorEmail: getAuthorEmail(),
        kind: "change",
        source: "Spark form",
        title: `${action === "published" ? "Published" : "Saved"} ${formName}`,
        detail: formatSparkFormSupportLogDetail(request.changes),
        applicationId: request.applicationId,
        applicationName: request.applicationName,
        organizationId: request.organizationId,
        organizationName: request.organizationName,
        notes: []
    };

    return createOrganizationVariable({
        name: createVariableName(variablePrefix, createdAt, id),
        value: JSON.stringify(entry),
        organizationId: targetOrganizationId
    });
};

export const recordSparkFormSupportHistory = async (
    request: SparkFormSupportHistoryRequest
): Promise<void> => {
    if (!request.applicationId || !request.organizationId || !request.changes?.length) {
        return;
    }

    try {
        const sharedResult = await writeSupportHistoryEntry(
            SHARED_SUPPORT_ORGANIZATION_ID,
            SUPPORT_LOG_VARIABLE_PREFIX,
            request
        );

        if (sharedResult.resultType === "Ok" || request.organizationId === SHARED_SUPPORT_ORGANIZATION_ID) {
            return;
        }
    } catch {
        // The local organization write below is the silent fallback.
    }

    try {
        await writeSupportHistoryEntry(
            request.organizationId,
            getSparkFormSupportLogPrefix(request.applicationId),
            request
        );
    } catch {
        // Support history is best-effort and must never affect the Spark save flow.
    }
};
